'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { theme } from '../(marketing)/_landing/theme';
import {
  readConsentClient,
  writeConsentClient,
  type ConsentValue,
} from '../../lib/consent';
import type { Locale } from '../../lib/locale';
import type { CookieConfig, CookieRow } from '../../lib/cookie-config';

// Pick locale from the URL pathname when it has a /pt or /en prefix; fall back
// to the server-resolved locale (cookie/headers) on routes without a locale
// segment like /sign-in or /dashboard.
function useLocaleFromPath(fallback: Locale): Locale {
  const pathname = usePathname();
  if (!pathname) return fallback;
  if (pathname === '/en' || pathname.startsWith('/en/')) return 'en';
  if (pathname === '/pt' || pathname.startsWith('/pt/')) return 'pt';
  return fallback;
}

// Listen for this event to programmatically re-open the banner -used by the
// floating cookie button and the footer "Manage cookies" link.
export const SHOW_CONSENT_EVENT = 'locusly:show-consent';

type CookieCategory = { label: string; intro: string; rows: CookieRow[] };

const COPY: Record<
  Locale,
  {
    title: string;
    body: string;
    accept: string;
    essential: string;
    showDetails: string;
    hideDetails: string;
    detailsTitle: string;
    closeAria: string;
    fabLabel: string;
    categories: { essential: CookieCategory; analytics: CookieCategory };
  }
> = {
  pt: {
    title: 'Cookies & privacidade',
    body:
      'Usamos cookies essenciais para o site funcionar (sessão, idioma) e cookies opcionais para perceber como melhorar a experiência. A escolha é tua, podes mudar de ideias a qualquer momento.',
    accept: 'Aceitar todas',
    essential: 'Apenas essenciais',
    showDetails: 'Ver detalhes',
    hideDetails: 'Ocultar detalhes',
    detailsTitle: 'O que guardamos',
    closeAria: 'Fechar',
    fabLabel: 'Definições de cookies',
    categories: {
      essential: {
        label: 'Essenciais (sempre ativas)',
        intro:
          'Necessárias para o site funcionar. Não te seguem entre sites e não precisam de consentimento sob a regra "estritamente necessárias" do RGPD/ePrivacy.',
        rows: [
          {
            name: 'locusly-consent',
            purpose: 'Guarda a tua escolha sobre cookies.',
            duration: '1 ano',
          },
          {
            name: 'locusly-locale',
            purpose: 'Lembra o idioma preferido (PT/EN).',
            duration: '1 ano',
          },
          {
            name: 'Cookies de sessão',
            purpose: 'Autenticação segura da tua conta (apenas quando tens sessão iniciada).',
            duration: 'Sessão',
          },
        ],
      },
      analytics: {
        label: 'Análise (opcionais)',
        intro:
          'Ajudam-nos a perceber que páginas funcionam e onde melhorar. São primeira-parte, agregadas e nunca partilhadas com publicidade.',
        rows: [
          {
            name: 'Eventos internos de produto',
            purpose:
              'Visitas, leads, cliques em CTAs, origens de tráfego. Guardados na nossa base de dados, sem fingerprinting.',
            duration: 'Até 24 meses',
          },
        ],
      },
    },
  },
  en: {
    title: 'Cookies & privacy',
    body:
      'We use essential cookies to make the site work (session, language) and optional cookies to learn how to improve the experience. Your call, you can change your mind any time.',
    accept: 'Accept all',
    essential: 'Only essential',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    detailsTitle: 'What we store',
    closeAria: 'Close',
    fabLabel: 'Cookie settings',
    categories: {
      essential: {
        label: 'Essential (always on)',
        intro:
          'Required for the site to work. They do not track you across sites and need no consent under the GDPR/ePrivacy "strictly necessary" rule.',
        rows: [
          {
            name: 'locusly-consent',
            purpose: 'Remembers your cookie choice.',
            duration: '1 year',
          },
          {
            name: 'locusly-locale',
            purpose: 'Remembers your preferred language (PT/EN).',
            duration: '1 year',
          },
          {
            name: 'Session cookies',
            purpose: 'Secure authentication for your account (only when signed in).',
            duration: 'Session',
          },
        ],
      },
      analytics: {
        label: 'Analytics (optional)',
        intro:
          'Help us see what works on the site and where to improve. First-party, aggregated, and never shared with ad platforms.',
        rows: [
          {
            name: 'Internal product events',
            purpose:
              'Page views, leads, CTA clicks, traffic sources. Stored in our database, no fingerprinting.',
            duration: 'Up to 24 months',
          },
        ],
      },
    },
  },
};

export function CookieConsent({ locale, config }: { locale: Locale; config?: CookieConfig | null }) {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const activeLocale = useLocaleFromPath(locale);
  const c = {
    accent:    config?.colors?.accent    ?? theme.c.accent,
    accentFg:  config?.colors?.accentFg  ?? theme.c.accentFg,
    surface:   config?.colors?.surface   ?? theme.c.surface,
    fg:        config?.colors?.fg        ?? theme.c.fg,
  };
  const baseCopy = COPY[activeLocale];
  const ov = config?.[activeLocale];
  const copy = {
    ...baseCopy,
    body: ov?.body ?? baseCopy.body,
    categories: {
      essential: {
        ...baseCopy.categories.essential,
        intro: ov?.essentialIntro ?? baseCopy.categories.essential.intro,
        rows: ov?.essentialRows ?? baseCopy.categories.essential.rows,
      },
      analytics: {
        ...baseCopy.categories.analytics,
        intro: ov?.analyticsIntro ?? baseCopy.categories.analytics.intro,
        rows: ov?.analyticsRows ?? baseCopy.categories.analytics.rows,
      },
    },
  };

  // Show on first visit, hide after a choice. The 600ms delay avoids competing
  // with the hero entrance animation on the landing page.
  useEffect(() => {
    setMounted(true);
    const stored = readConsentClient();
    if (stored) return;

    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // Allow other parts of the app (e.g., footer "Manage cookies" link) to
  // re-open the banner without a page reload.
  useEffect(() => {
    function onShow() {
      setVisible(true);
    }
    window.addEventListener(SHOW_CONSENT_EVENT, onShow);
    return () => window.removeEventListener(SHOW_CONSENT_EVENT, onShow);
  }, []);

  function decide(value: ConsentValue) {
    writeConsentClient(value, config?.cookieDomain ?? undefined);
    setVisible(false);
    setShowDetails(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('locusly:consent-decided'));
    }
  }

  if (!mounted || !visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="lp-consent-title"
      aria-describedby="lp-consent-body"
      className="lp-consent-banner"
      style={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        right: 24,
        zIndex: 100,
        maxWidth: 720,
        margin: '0 auto',
        background: c.surface,
        color: c.fg,
        border: `1px solid ${theme.c.borderStrong}`,
        borderRadius: theme.radii.xl,
        boxShadow:
          '0 30px 60px -20px rgba(27,23,20,0.35), 0 12px 24px -10px rgba(27,23,20,0.18)',
        padding: '22px 26px 20px',
        fontFamily: theme.fonts.body,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 14,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 36px',
            width: 36,
            height: 36,
            borderRadius: 999,
            background: theme.c.chip,
            color: c.accent,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            marginTop: 2,
          }}
        >
          🍪
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            id="lp-consent-title"
            style={{
              fontFamily: theme.fonts.display,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: -0.3,
              color: c.fg,
              marginBottom: 6,
            }}
          >
            {copy.title}
          </div>
          <p
            id="lp-consent-body"
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: theme.c.fgMuted,
            }}
          >
            {copy.body}
          </p>
          <button
            type="button"
            onClick={() => setShowDetails((s) => !s)}
            aria-expanded={showDetails}
            aria-controls="lp-consent-details"
            style={{
              marginTop: 10,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
              fontSize: 13,
              color: c.accent,
              textDecoration: 'underline',
              textDecorationThickness: 1,
              textUnderlineOffset: 3,
            }}
          >
            {showDetails ? copy.hideDetails : copy.showDetails}
          </button>
        </div>
      </div>

      {showDetails && (
        <div
          id="lp-consent-details"
          style={{
            marginTop: 4,
            marginBottom: 16,
            padding: '14px 16px',
            background: theme.c.bgAlt,
            borderRadius: theme.radii.md,
            border: `1px solid ${theme.c.border}`,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              fontFamily: theme.fonts.mono,
              fontSize: 10,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: theme.c.fgFaint,
              marginBottom: 12,
            }}
          >
            {copy.detailsTitle}
          </div>
          {(['essential', 'analytics'] as const).map((catKey) => {
            const cat = copy.categories[catKey];
            return (
              <div key={catKey} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontFamily: theme.fonts.body,
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.c.fg,
                    marginBottom: 4,
                  }}
                >
                  {cat.label}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: theme.c.fgMuted,
                    marginBottom: 8,
                  }}
                >
                  {cat.intro}
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {cat.rows.map((r) => (
                    <li
                      key={r.name}
                      style={{
                        fontSize: 12,
                        lineHeight: 1.45,
                        color: theme.c.fgMuted,
                        paddingLeft: 10,
                        borderLeft: `2px solid ${theme.c.border}`,
                      }}
                    >
                      <code
                        style={{
                          fontFamily: theme.fonts.mono,
                          fontSize: 11,
                          color: theme.c.fg,
                          background: 'transparent',
                        }}
                      >
                        {r.name}
                      </code>
                      <span style={{ margin: '0 6px', color: theme.c.fgFaint }}>·</span>
                      <span>{r.purpose}</span>
                      <span style={{ margin: '0 6px', color: theme.c.fgFaint }}>·</span>
                      <span style={{ color: theme.c.fgFaint }}>{r.duration}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => decide('essential')}
          className="lp-btn-ghost"
          style={{
            background: 'transparent',
            color: c.fg,
            fontFamily: theme.fonts.body,
            fontSize: 14,
            fontWeight: 500,
            padding: '10px 18px',
            borderRadius: theme.radii.pill,
            border: `1px solid ${theme.c.borderStrong}`,
            cursor: 'pointer',
          }}
        >
          {copy.essential}
        </button>
        <button
          type="button"
          onClick={() => decide('accepted')}
          className="lp-btn-primary"
          style={{
            background: c.accent,
            color: c.accentFg,
            border: 'none',
            fontFamily: theme.fonts.body,
            fontSize: 14,
            fontWeight: 500,
            padding: '10px 22px',
            borderRadius: theme.radii.pill,
            boxShadow: '0 12px 24px -10px rgba(200,85,61,0.45)',
            cursor: 'pointer',
          }}
        >
          {copy.accept}
        </button>
      </div>

      <style>{`
        .lp-consent-banner {
          animation: lpConsentSlideUp 480ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes lpConsentSlideUp {
          from {
            opacity: 0;
            transform: translate3d(0, 24px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .lp-consent-banner { animation: none; }
        }
        @media (max-width: 640px) {
          .lp-consent-banner {
            bottom: 12px !important;
            left: 12px !important;
            right: 12px !important;
            padding: 18px 18px 16px !important;
          }
        }
      `}</style>
    </div>
  );
}

// Small "Manage cookies" button -drop into the footer. Dispatches the
// custom event the banner listens for, so users can re-open it any time.
export function ManageCookiesLink({
  children,
  style,
  className,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(SHOW_CONSENT_EVENT));
        }
      }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// Floating, always-visible cookie settings button. Bottom-left corner so it
// stays out of the way of right-side chat widgets / scroll-to-top affordances.
// Hidden while the consent banner itself is open to avoid two overlapping CTAs.
export function CookieFloatingButton({ locale }: { locale: Locale }) {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const activeLocale = useLocaleFromPath(locale);
  const label = COPY[activeLocale].fabLabel;

  useEffect(() => {
    setMounted(true);
    // First-visit handling: the banner auto-opens after 600ms when no consent
    // cookie exists. Start the FAB hidden in that case so the two CTAs don't
    // overlap. After the user decides, the consent-decided event flips it on.
    const stored = readConsentClient();
    if (!stored) setBannerOpen(true);

    function onShow() {
      setBannerOpen(true);
    }
    function onHide() {
      setBannerOpen(false);
    }
    window.addEventListener(SHOW_CONSENT_EVENT, onShow);
    window.addEventListener('locusly:consent-decided', onHide);
    return () => {
      window.removeEventListener(SHOW_CONSENT_EVENT, onShow);
      window.removeEventListener('locusly:consent-decided', onHide);
    };
  }, []);

  if (!mounted || bannerOpen) return null;

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        window.dispatchEvent(new Event(SHOW_CONSENT_EVENT));
      }}
      className="lp-cookie-fab"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 90,
        width: 42,
        height: 42,
        borderRadius: 999,
        background: theme.c.surface,
        border: `1px solid ${theme.c.borderStrong}`,
        boxShadow: '0 12px 24px -8px rgba(27,23,20,0.18)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.c.fg,
        padding: 0,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3a9 9 0 1 0 9 9 5 5 0 0 1-5-5 4 4 0 0 1-4-4z" />
        <circle cx="9" cy="11" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="13.5" cy="14.5" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="15.5" cy="9" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="8" cy="15.5" r="0.9" fill="currentColor" stroke="none" />
      </svg>
      <style>{`
        .lp-cookie-fab {
          transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 200ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lp-cookie-fab:hover {
          transform: translate3d(0, -2px, 0);
          box-shadow: 0 18px 32px -10px rgba(27,23,20,0.22);
        }
        @media (max-width: 640px) {
          .lp-cookie-fab {
            bottom: 14px !important;
            right: 14px !important;
          }
        }
      `}</style>
    </button>
  );
}
